import {
  IntakeInterviewStatusEnum,
  StepStateEnum,
  TranscriptStateEnum,
} from "@app_schema";

import { type CaseStatusTone } from "@styled/case_steps";

// The document types the project-detail section selector can choose. One union,
// threaded through the case page's sections, project_documents, projects_details,
// and the assistant's scope mapping so tsc catches any section-key mismatch.
export type IntakeType =
  | "records"
  | "finalReport"
  | "drafting"
  | "intakeInterview"
  | "dictation"
  | "supporting";

// A step's mark, its state in words, and the full state for its title and
// screen readers ("Records: ready").
type Line = { tone: CaseStatusTone; state: string; title: string };

const line = (step: string, tone: CaseStatusTone, state: string): Line => ({
  tone,
  state,
  title: `${step}: ${state.charAt(0).toLowerCase()}${state.slice(1)}`,
});

// The server's step states, read as one mark each.
const recordsLine = (state?: StepStateEnum): Line => {
  switch (state) {
    case StepStateEnum.Done:
      return line("Records", "done", "Ready");
    case StepStateEnum.Processing:
      return line("Records", "working", "Reading");
    case StepStateEnum.Failed:
      return line("Records", "failed", "Failed");
    case StepStateEnum.Ready:
      return line("Records", "todo", "Uploaded, not read yet");
    default:
      return line("Records", "todo", "Not uploaded yet");
  }
};

const reportLine = (state?: StepStateEnum): Line => {
  switch (state) {
    case StepStateEnum.Ready:
      return line("Report", "done", "Ready");
    case StepStateEnum.Processing:
      return line("Report", "working", "Reading");
    case StepStateEnum.Failed:
      return line("Report", "failed", "Failed");
    default:
      return line("Report", "todo", "Not uploaded yet");
  }
};

const draftingLine = (state?: StepStateEnum | null): Line => {
  switch (state) {
    case StepStateEnum.Ready:
      return line("Drafting", "done", "Draft ready");
    case StepStateEnum.Processing:
      return line("Drafting", "working", "Reading");
    case StepStateEnum.Failed:
      return line("Drafting", "failed", "Failed");
    default:
      return line("Drafting", "todo", "No draft yet");
  }
};

const intakeLine = (status?: IntakeInterviewStatusEnum): Line => {
  switch (status) {
    case IntakeInterviewStatusEnum.Finished:
      return line("Intake", "done", "Finished");
    case IntakeInterviewStatusEnum.Started:
      return line("Intake", "working", "In progress");
    case IntakeInterviewStatusEnum.Declined:
      return line("Intake", "todo", "Claimant declined");
    case IntakeInterviewStatusEnum.LinkReady:
      return line("Intake", "todo", "Link made, not started");
    default:
      return line("Intake", "todo", "No link yet");
  }
};

const dictationLine = (state?: TranscriptStateEnum | null): Line => {
  switch (state) {
    case TranscriptStateEnum.Ready:
      return line("Dictation", "done", "Ready");
    case TranscriptStateEnum.Transcribing:
      return line("Dictation", "working", "Transcribing");
    case TranscriptStateEnum.Failed:
      return line("Dictation", "failed", "Failed");
    default:
      return line("Dictation", "todo", "None yet");
  }
};

export type CaseStepKey = Exclude<IntakeType, "supporting">;

// The `?section=` that opens each step: the cases list links with it, and so
// does Save on Edit case.
export const SECTION_PARAM: Record<CaseStepKey, string> = {
  records: "records",
  intakeInterview: "intake",
  dictation: "dictation",
  drafting: "drafting",
  finalReport: "report",
};

// And back: the step a `?section=` opens.
export const SECTION_PARAMS = new Map(
  Object.entries(SECTION_PARAM).map(([key, param]) => [
    param,
    key as CaseStepKey,
  ]),
);

export type CaseStepLine = Line & {
  key: CaseStepKey;
  // The case page's name for the step, and the cases list's shorter one.
  label: string;
  short: string;
};

// A case's steps in order: Records · Intake interview · Dictation · Drafting ·
// Report, Intake interview and Drafting only while the org's flags are on.
export const caseSteps = (project: {
  stepStates?: { process: StepStateEnum; finalize: StepStateEnum } | null;
  draftingEnabled: boolean;
  draftingState?: StepStateEnum | null;
  intakeInterviewEnabled: boolean;
  intakeInterview?: { status: IntakeInterviewStatusEnum } | null;
  dictationState?: TranscriptStateEnum | null;
}): CaseStepLine[] => [
  {
    key: "records",
    label: "Records",
    short: "Records",
    ...recordsLine(project.stepStates?.process),
  },
  ...(project.intakeInterviewEnabled
    ? [
        {
          key: "intakeInterview" as const,
          label: "Intake interview",
          short: "Intake",
          ...intakeLine(project.intakeInterview?.status),
        },
      ]
    : []),
  {
    key: "dictation",
    label: "Dictation",
    short: "Dictation",
    ...dictationLine(project.dictationState),
  },
  ...(project.draftingEnabled
    ? [
        {
          key: "drafting" as const,
          label: "Drafting",
          short: "Drafting",
          ...draftingLine(project.draftingState),
        },
      ]
    : []),
  {
    key: "finalReport",
    label: "Report",
    short: "Report",
    ...reportLine(project.stepStates?.finalize),
  },
];
